import { FC, useCallback, useEffect, useMemo, useState, EffectCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Breadcrumbs, Button, Icon, Input, LottieLoader, Notification } from '@mondra/ui-components'
import { useFormik } from 'formik'
import Collapsible from 'react-collapsible'
import { fetchPackagingProcess, getConfiguration } from './packagingProcessActions'
import { sortDropdown } from '../../../../shared/formFunctions'
import DataQualitySection, {
  PACKAGING_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION,
  PACKAGING_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION,
} from '../../../../components/ProcessingActivitySections/DataQualitySection'
import {
  formSchema,
  mapMaterialRowToIngredient,
  mapComponentStateToProduct,
  buildFlowTriggerLabel,
} from './utils'
import {
  updatePackagingProcessingActivity,
  publishPackagingProcessingActivity,
  unPublishPackagingProcessingActivity,
  createPackagingProcessingActivity,
} from './api'
import { usePrompt } from '../../../../hooks/usePrompt'
import {
  handlePublishStatus,
  setDefaultPackagingProcess,
} from '../../../../redux/packagingProcessSlice'
import InputItem from '../../../../components/InputItem'
import ElementaryFlowForm from './components/ElementaryFlowForm'
import { addLocalFormNote } from '../../../../shared/noteFunctions'
import { showCorrelationIdError } from '../../../../redux/correlationIdSlice'
import ActivityRightPanel from '../../../../components/ActivityRightPanel'
import UnsavedChangesWarning from '../../../../components/UnsavedChangesWarning'
import { ActivityTypes, ROUTES } from '../../../../const'
import './index.scss'

// A publish rejection carries FluentValidation errors, not a ProblemDetails title: reading .title
// alone shows an empty notification for exactly the failures worth reading.
const toServerErrorMessage = (error: any): string => {
  const data = error.response?.data

  if (typeof data === 'string') {
    return data
  }
  if (Array.isArray(data)) {
    return data
      .map((item: any) => item.errorMessage ?? item.ErrorMessage)
      .filter(Boolean)
      .join('\n')
  }
  if (data?.errors && typeof data.errors !== 'string') {
    return Object.values(data.errors).flat().join('\n')
  }
  return data?.title ?? data?.errors ?? error.message ?? 'Something went wrong.'
}

const PackagingProcess: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    loading,
    packagingProcess: process,
    configuration,
  } = useAppSelector(state => state.packagingProcessSlice)
  const [showNotification, setShowNotification] = useState<boolean>(false)
  const [showError, setShowError] = useState<string>('')
  const [errors, setErrors] = useState<[any, string[]][]>()
  const [showPublished, setShowPublished] = useState<boolean>(false)
  const processId = searchParams.get('itemId') || ''

  const resourceSources = configuration.resourceSources

  // Both flags, per 36998: an elementary flow is selectable only when Science has marked the
  // resource-source as an emission AND as an elementary flow. 37042's AC and bug 39320 read this
  // as OR; 36998 is the rule that stands (confirmed 2026-09-23).
  const elementaryFlowOptions = useMemo(
    () => resourceSources.filter(el => el.isElementaryFlow),
    [resourceSources]
  )

  useEffect((): ReturnType<EffectCallback> => {
    if (processId === '') {
      navigate('/not-found')
      return
    }
    dispatch(getConfiguration())
    if (processId !== 'new') {
      dispatch(fetchPackagingProcess(processId, () => navigate(ROUTES.packagingProcessing)))
    }
    return () => {
      dispatch(setDefaultPackagingProcess())
    }
    // Depends on [packagingProcess] only per the FE spec — the food module's [configuration,
    // process] effect can loop; guard against that here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formik = useFormik({
    initialValues: { ...process },
    validationSchema: formSchema,
    onSubmit: async (values, { setSubmitting }) => {
      // Nothing to publish until the record exists.
      if (processId === 'new') {
        setSubmitting(false)
        return
      }

      const isPublishing = process.state === 0

      try {
        if (isPublishing) {
          await publishPackagingProcessingActivity(processId)
          dispatch(handlePublishStatus(1))
        } else {
          await unPublishPackagingProcessingActivity(processId)
          dispatch(handlePublishStatus(0))
        }
        setShowPublished(true)
        setTimeout(() => setShowPublished(false), 4000)
      } catch (error: any) {
        setShowError(toServerErrorMessage(error))
      } finally {
        // Formik leaves isSubmitting true for a handler that does not settle it, which would
        // disable Save for the rest of the page's life.
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    formik.setValues({ ...process })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process])

  const isEndOfLife = Boolean(formik.values.isEndOfLife)

  const totalOutput = useMemo(() => {
    let total = 0
    formik.values.waste.forEach(el => (total += Number(el.quantity) || 0))
    formik.values.outputElementaryFlows.forEach(el => (total += Number(el.quantity) || 0))
    return total
  }, [formik.values.waste, formik.values.outputElementaryFlows])

  const handleCancel = useCallback(() => navigate(ROUTES.packagingProcessing), [navigate])

  const saveDraft = useCallback(
    async e => {
      e.preventDefault()
      if (formik.isSubmitting) {
        return
      }
      formik.setSubmitting(true)
      const { values } = formik

      // D13 (corrected 2026-09-10): elementaryFlows/outputElementaryFlows are sent as their own
      // dedicated wire properties (inputElementaryFlows/outputElementaryFlows), matching BE's two
      // new dedicated collections — NOT merged into resourceSources/waste. No merge-on-submit.
      const payload = {
        ...values,
        // D3: BE's Materials alias is the shared Ingredients DTO shape (InputIngredientId/Type/
        // Level), not PackagingMaterialCategoryId/FunctionalUnitId — translate before sending.
        materials: values.materials.map(mapMaterialRowToIngredient),
        // D4/D5: BE's Component alias is a single ProductQuantityDTO, not a flat
        // packagingComponentId/componentScalingFactor pair — translate before sending.
        component: mapComponentStateToProduct(values),
        inputElementaryFlows: values.elementaryFlows,
        packagingComponentId: values.packagingComponentId ? +values.packagingComponentId : null,
        referenceDataPublicationYear: values.referenceDataPublicationYear
          ? +values.referenceDataPublicationYear
          : null,
        referenceRegionIdentifier:
          !values.referenceRegionIdentifier && values.referenceRegionIdentifier !== 0
            ? null
            : +values.referenceRegionIdentifier,
        secondaryDataReferenceSourceType:
          !values.secondaryDataReferenceSourceType && values.secondaryDataReferenceSourceType !== 0
            ? null
            : +values.secondaryDataReferenceSourceType,
        referencesSamplingYear: values.referencesSamplingYear
          ? Number(values.referencesSamplingYear)
          : null,
        referenceSourcingCountry:
          !values.referenceSourcingCountry && values.referenceSourcingCountry !== 0
            ? null
            : +values.referenceSourcingCountry,
        referenceTechnologyProxyContribution:
          values.referenceTechnologyProxyContribution !== null
            ? +values.referenceTechnologyProxyContribution
            : null,
      }
      delete (payload as any).elementaryFlows
      // activityName is derived/display-only (BE plan O1) — no matching DTO property on BE, so
      // don't send it; strip it here rather than let BE's model binder silently drop it.
      delete (payload as any).activityName

      if (processId === 'new') {
        try {
          await createPackagingProcessingActivity(payload).then(() => {
            navigate(ROUTES.packagingProcessing)
          })
        } catch (error: any) {
          if (error.showCorrelationId) {
            dispatch(showCorrelationIdError(error.CorrelationId))
          } else {
            setShowError(
              typeof error.response.data === 'string'
                ? error.response.data
                : typeof error.response.data.errors === 'string'
                ? error.response.data.errors
                : Object.values(error.response.data.errors).toString().replace(/!,/g, '!\n')
            )
            setTimeout(() => setShowError(''), 5000)
            setErrors(
              typeof error.response.data.errors === 'string'
                ? error.response.data.errors
                : Object.entries(error.response.data.errors)
            )
          }
        } finally {
          formik.setSubmitting(false)
        }
      } else {
        try {
          await updatePackagingProcessingActivity(processId, payload as any)
            .then(() =>
              dispatch(fetchPackagingProcess(processId, () => navigate(ROUTES.packagingProcessing)))
            )
            .then(() => {
              setShowNotification(true)
              setTimeout(() => setShowNotification(false), 5000)
            })
        } catch (error: any) {
          if (error.showCorrelationId) {
            dispatch(showCorrelationIdError(error.CorrelationId))
          } else {
            setShowError(
              typeof error.response.data === 'string'
                ? error.response.data
                : typeof error.response.data.errors === 'string'
                ? error.response.data.errors
                : Object.values(error.response.data.errors).toString().replace(/!,/g, '!\n')
            )
            setTimeout(() => setShowError(''), 5000)
            setErrors(
              typeof error.response.data.errors === 'string'
                ? error.response.data.errors
                : Object.entries(error.response.data.errors)
            )
          }
        } finally {
          formik.setSubmitting(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik, processId, dispatch, navigate]
  )

  // Zero resources is legitimate (the processing-impacts case) — do not gate on
  // resourceSources.length like food does.
  const disableRequest = formik.isSubmitting

  const isDirty = JSON.stringify(process) !== JSON.stringify(formik.values)

  usePrompt(
    'There are unsaved changes, do you want to leave?',
    processId !== 'new' ? isDirty : false
  )

  const addProcess = useCallback(
    (values, array) => {
      formik.setValues({
        ...formik.values,
        [array]: [...formik.values[array], values],
      })
    },
    [formik]
  )

  const deleteProcess = useCallback(
    (idx, array) => {
      const newComponents = formik.values[array].filter((_el, index) => index !== +idx)
      formik.setValues({
        ...formik.values,
        [array]: newComponents,
      })
    },
    [formik]
  )

  // Single stable handler for every repeating-section delete button, index/array read off
  // data-* attributes rather than a fresh per-row inline arrow — avoids recreating a closure on
  // every render for each row (materials/resourceSources/elementaryFlows/waste/outputElementaryFlows
  // all share this one reference).
  const handleDeleteRowClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const { index, array } = e.currentTarget.dataset
      if (index !== undefined && array) {
        deleteProcess(Number(index), array)
      }
    },
    [deleteProcess]
  )

  const handleProcessFlowChange = useCallback(e => formik.handleChange(e), [formik])
  const handleAdditionalInfoChange = useCallback(e => formik.handleChange(e), [formik])
  const handleDefaultLossFactorChange = useCallback(e => formik.handleChange(e), [formik])
  const handleEndOfLifeChange = useCallback(
    (checked: boolean) => formik.setFieldValue('isEndOfLife', checked),
    [formik]
  )
  const handleDataQualityFieldChange = useCallback(e => formik.handleChange(e), [formik])

  if (loading === true && processId !== 'new') {
    return (
      <div className="overlay">
        <LottieLoader
          lottieType="butterflyLottie"
          style={{ height: '10.25rem', width: '10.25rem' }}
        />
      </div>
    )
  }

  return (
    <>
      <div className="piece content">
        {showError !== '' && (
          <div className={'piece-notification z-dialog'}>
            <Notification type="error" label="Oops:" description={showError} />
          </div>
        )}
        {showPublished && (
          <div className={'piece-notification z-dialog'}>
            <Notification
              type="success"
              label="Success:"
              description="Status was updated!"
              isToast={false}
            />
          </div>
        )}
        {showNotification && (
          <div className={'piece-notification z-dialog'}>
            <Notification
              type="success"
              label="Success:"
              description="Item was updated!"
              isToast={false}
            />
          </div>
        )}
        <div className="mb-3 mt-3">
          <Breadcrumbs
            routes={[
              { path: ROUTES.packagingProcessing, title: 'Processing Activity Table' },
              {
                path: `${ROUTES.packagingProcessingItem}?itemId=${processId}`,
                title: 'Processing Activity Item',
              },
            ]}
          />
        </div>
        {processId !== 'new' && (
          <div className="piece-header">
            <div
              className={`piece-header-status ${
                process.state === 0 ? 'bg-red-500' : 'bg-emerald-500'
              }`}
            >
              {process.state === 0 ? 'Not Published' : 'Published'}
            </div>
            {process?.lastPublishedDate && (
              <span className={'inputItem-header'}>
                {`Last published date: ${new Date(process.lastPublishedDate).toLocaleString()}`}
              </span>
            )}
          </div>
        )}

        <p className={'piece-description mb-7 mt-1 decoration-gray-600'}>
          This database is a collation of resource type and consumption involved in the processing
          of different packaging components. All processing activity data has been reported for a
          functional unit of <b>1 kg of the output component</b>.
        </p>

        <form onSubmit={formik.handleSubmit} className="piece-content">
          <h5 className={'mb-5 mt-5'}>Activity Type</h5>
          <h3>Activity Name</h3>
          <p className={'mb-1 decoration-gray-600'}>
            The activity name is the process flow. E.g. Injection Moulding.
          </p>
          <Input
            disabled={true}
            className={'mb-5 mt-2 h-20 w-full'}
            value={formik.values.processFlow}
          />
          <InputItem
            type="input"
            name="Process Flow"
            description="Please review the excel file called “Mondra_Processing Activities_Ontology” to list the process flow steps that this activity goes through, separate by a comma. E.g. Mixing, Shaping, Injection Moulding, Blow Moulding, Cleaning, Cooling."
            className={'mb-5 w-full'}
            formik={formik}
            showAsterisk
            defaultValue={formik.values.processFlow}
            onChange={handleProcessFlowChange}
            formikName="processFlow"
          />
          <InputItem
            type="input"
            name="Additional Supplementary Information"
            description="Please add the Processing LCI file URL for any additional calculation, or information about modification on existing Ecoinvent processes"
            className={'mb-5 w-full'}
            defaultValue={formik.values.additionalSupplementaryInformation || ''}
            onChange={handleAdditionalInfoChange}
            formikName="additionalSupplementaryInformation"
          />
          <InputItem
            name="External code"
            description=""
            type="input"
            disabled
            className={'mb-5 mt-2 h-20 w-full'}
            formik={formik}
            onChange={formik.handleChange}
            defaultValue={formik.values.code || ''}
            formikName="code"
          />
          <InputItem
            name="End of life"
            description="Toggle on if this processing activity models end-of-life treatment."
            type="toggle"
            onToggleChange={handleEndOfLifeChange}
            formikName="isEndOfLife"
            formik={formik}
            className={'mb-5 w-full'}
            defaultValue={isEndOfLife}
          />

          <div className={'piece-content-collapsible'}>
            <h2>Inputs</h2>
            <InputItem
              type="input"
              name="Default material loss factor"
              description="Enter the loss scaling factor directly, e.g. 1.05 for 5% loss. This is not a percentage and is not auto-calculated."
              className={'mb-5 mt-5 w-full'}
              formik={formik}
              showAsterisk
              defaultValue={formik.values.defaultMaterialLossFactor}
              onChange={handleDefaultLossFactorChange}
              formikName="defaultMaterialLossFactor"
            />

            <h5 className={'mt-8'}>Elementary flows</h5>
            {formik.values.elementaryFlows.map((row, index: number) => (
              <Collapsible
                key={row.id ?? index}
                trigger={
                  <div className="collapsible_trigger">
                    <span>
                      {buildFlowTriggerLabel(
                        elementaryFlowOptions,
                        row.resourceSourceId,
                        row.quantity
                      )}
                    </span>
                    <Icon className="collapsible_icon" type="chevronDown" />
                  </div>
                }
              >
                <div className="piece-content-inputs">
                  <InputItem
                    name="Elementary flow type"
                    description=""
                    type="select"
                    formik={formik}
                    showAsterisk
                    onChange={formik.handleChange}
                    formikName={`elementaryFlows[${index}].resourceSourceId`}
                    defaultValue={row.resourceSourceId}
                    options={sortDropdown(
                      elementaryFlowOptions,
                      row.resourceSourceId ?? -1,
                      'combinedName'
                    )}
                  />
                  <InputItem
                    name="Quantity"
                    description=""
                    type="input"
                    formik={formik}
                    showAsterisk
                    onChange={formik.handleChange}
                    defaultValue={row.quantity}
                    formikName={`elementaryFlows[${index}].quantity`}
                    addonTitle={
                      elementaryFlowOptions.find(el => el.id === row.resourceSourceId)?.unit
                    }
                    addLocal={params =>
                      addLocalFormNote({
                        formik,
                        arrayName: 'elementaryFlows',
                        componentIndex: index,
                        ...params,
                      })
                    }
                    notes={row.quantityNotes || []}
                  />
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={handleDeleteRowClick}
                    data-index={index}
                    data-array="elementaryFlows"
                    variant="danger"
                    iconType="close"
                  >
                    Delete
                  </Button>
                </div>
              </Collapsible>
            ))}
            <ElementaryFlowForm
              addProcess={addProcess}
              resourceSources={elementaryFlowOptions}
              targetArray="elementaryFlows"
            />
          </div>

          <div className={'piece-content-collapsible'}>
            <h2>Outputs</h2>
            <h5 className={'mt-5'}>Elementary flows</h5>
            {formik.values.outputElementaryFlows.map((row, index: number) => (
              <Collapsible
                key={row.id ?? index}
                trigger={
                  <div className="collapsible_trigger">
                    <span>
                      {buildFlowTriggerLabel(
                        elementaryFlowOptions,
                        row.resourceSourceId,
                        row.quantity
                      )}
                    </span>
                    <Icon className="collapsible_icon" type="chevronDown" />
                  </div>
                }
              >
                <div className="piece-content-inputs">
                  <InputItem
                    name="Elementary flow type"
                    description=""
                    type="select"
                    formik={formik}
                    showAsterisk
                    onChange={formik.handleChange}
                    formikName={`outputElementaryFlows[${index}].resourceSourceId`}
                    defaultValue={row.resourceSourceId}
                    options={sortDropdown(
                      elementaryFlowOptions,
                      row.resourceSourceId ?? -1,
                      'combinedName'
                    )}
                  />
                  <InputItem
                    name="Quantity"
                    description=""
                    type="input"
                    formik={formik}
                    showAsterisk
                    onChange={formik.handleChange}
                    defaultValue={row.quantity}
                    formikName={`outputElementaryFlows[${index}].quantity`}
                    addonTitle={
                      elementaryFlowOptions.find(el => el.id === row.resourceSourceId)?.unit
                    }
                    addLocal={params =>
                      addLocalFormNote({
                        formik,
                        arrayName: 'outputElementaryFlows',
                        componentIndex: index,
                        ...params,
                      })
                    }
                    notes={row.quantityNotes || []}
                  />
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={handleDeleteRowClick}
                    data-index={index}
                    data-array="outputElementaryFlows"
                    variant="danger"
                    iconType="close"
                  >
                    Delete
                  </Button>
                </div>
              </Collapsible>
            ))}
            <ElementaryFlowForm
              addProcess={addProcess}
              resourceSources={elementaryFlowOptions}
              targetArray="outputElementaryFlows"
            />
          </div>

          <h3>Total output</h3>
          <p className={'mb-1 decoration-gray-600'}>
            Please use as sense check for mass balance. The total input should equal the total
            output.
          </p>
          <Input
            disabled={true}
            className={'mb-5 mt-2 h-20 w-full'}
            value={totalOutput}
            addonTitle="kg"
          />

          <DataQualitySection
            referenceTechnologyTypeDescription={PACKAGING_REFERENCE_TECHNOLOGY_TYPE_DESCRIPTION}
            referenceTechnologyProxyContributionDescription={
              PACKAGING_REFERENCE_TECHNOLOGY_PROXY_CONTRIBUTION_DESCRIPTION
            }
            formik={formik}
            configuration={configuration}
            process={process}
            onFieldChange={handleDataQualityFieldChange}
          />

          {errors &&
            (Array.isArray(errors) ? (
              <ul className="piece-errors">
                {errors.map(([key, value]) =>
                  (value as any).map(err => (
                    <li key={key + err}>
                      <b>{key}:</b> {err}
                    </li>
                  ))
                )}
              </ul>
            ) : (
              <ul className="piece-errors">
                <li>{errors}</li>
              </ul>
            ))}

          <div className="piece-actions">
            <UnsavedChangesWarning isDirty={isDirty} />
            <Button variant="secondary" className="w-32" size="md" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-32"
              size="md"
              onClick={saveDraft}
              disabled={formik.isSubmitting}
            >
              Save
            </Button>
            {sessionStorage.getItem('permission') === 'true' && processId !== 'new' && (
              <Button
                variant="tertiary"
                className="w-32"
                size="md"
                type="submit"
                disabled={disableRequest || isDirty}
              >
                {process.state === 0 ? 'Publish' : 'Unpublish'}
              </Button>
            )}
          </div>
        </form>
      </div>
      <ActivityRightPanel
        activityId={processId}
        activityType={ActivityTypes.PackagingProcessingActivity}
        auditCreatedBy={process.researcherName}
        auditLastUpdatedBy={process.lastUpdatedName}
        companyId={formik.values.companyId}
        isVerified={formik.values.isVerified}
        verificationComments={formik.values.verificationComments}
        verifiedByName={formik.values.verifiedByName}
        verifiedOn={formik.values.verifiedOn}
        onChange={formik.handleChange}
      />
    </>
  )
}

export default PackagingProcess
